import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { red } from '@avangard/ui/colors';
import { Autocomplete, Button } from '@avangard/ui/core';
import { Star } from '@material-ui/icons';

import { FormStyled } from '@modules/layout/styled';
import { Form } from '@modules/layout/organisms';
import { useAccessOLCourses } from '@modules/courses/hooks';
import { useCurrentLap, useLazyAccessLaps, useLapYears } from '@modules/lap/hooks';

import type { LapEntity } from '@modules/lap/entities';
import type { CourseEntity } from '@modules/courses/entities';

type ChangeLapFormProps = {
    onCancel: () => void;
};

const StyledForm = styled(Form)`
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    width: 100%;

    > *:not(:last-child) {
        margin-bottom: 24px;
    }
`;

const FormRowSubmit = styled(FormStyled.FormRowSubmit)`
    justify-content: flex-end;
`;

const LapOption = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const starIcon = <Star style={{ fontSize: 16, color: red[100] }} />;

const ChangeLapForm = (props: ChangeLapFormProps): React.ReactElement => {
    const { onCancel } = props;

    const { t } = useTranslation(['common', 'lap']);

    const { values, errors, setFieldValue, isSubmitting } = useFormikContext<{
        course: CourseEntity;
        year: number;
        lap: LapEntity;
    }>();

    const { currentLap, loading: currentLapLoading } = useCurrentLap();
    const { lapYears, count: lapYearsCount, loading: lapYearsLoading } = useLapYears();

    const canChooseLap = !!values.course && !!values.year && lapYearsCount !== 0;

    const { courses, loading: coursesLoading } = useAccessOLCourses({
        fetchPolicy: 'network-only',
    });

    const [getAccessLaps, { laps, loading: lapsLoading }] = useLazyAccessLaps({
        fetchPolicy: 'network-only',
    });

    const handleChangeAutocomplete =
        <T extends any>(field: string) =>
        (_: React.ChangeEvent<{}>, value: T | null) => {
            setFieldValue(field, value);

            if (field === 'course' || field === 'year') {
                setFieldValue('lap', null);
            }
        };

    React.useEffect(() => {
        if (!values.course || !values.year) {
            return;
        }

        getAccessLaps({
            variables: {
                args: {
                    filter: {
                        courseId: values.course.id,
                        year: `${values.year}`,
                    },
                    page: { all: true },
                },
            },
        });
    }, [values.course, values.year]);

    return (
        <StyledForm>
            <Autocomplete<CourseEntity | null, false, false, false>
                fullWidth
                required
                id='course'
                label={t('common:forms.labels.course') ?? ''}
                placeholder={t('common:forms.placeholder.choose_course') ?? ''}
                options={courses}
                value={values.course}
                error={!!errors.course}
                helperText={errors.course}
                disabled={coursesLoading}
                getOptionLabel={option => option?.name ?? '-'}
                onChange={handleChangeAutocomplete<CourseEntity>('course')}
            />

            <Autocomplete<number | null, false, false, false>
                fullWidth
                required
                id='year'
                label={t('common:forms.labels.education_year') ?? ''}
                placeholder={t('common:forms.placeholder.choose_education_year') ?? ''}
                options={lapYears}
                value={values.year}
                error={!!errors.year}
                helperText={errors.year}
                disabled={currentLapLoading || lapYearsLoading}
                getOptionLabel={option => `${option}` || '-'}
                getOptionSelected={option => values.year === option}
                onChange={handleChangeAutocomplete<number>('year')}
            />

            <Autocomplete<LapEntity | null, false, false, false>
                fullWidth
                required
                id='lap'
                label={t('common:forms.labels.lap') ?? ''}
                placeholder={t('common:forms.placeholder.choose_lap') ?? ''}
                options={laps}
                value={values.lap}
                error={!!errors.lap}
                helperText={errors.lap}
                disabled={currentLapLoading || lapsLoading || !canChooseLap}
                getOptionLabel={option => option?.getFullDate() ?? '-'}
                renderOption={option => (
                    <LapOption>
                        {option?.getFullDate() ?? '-'}
                        {option?.isActiveLap(currentLap) ? starIcon : null}
                    </LapOption>
                )}
                onChange={handleChangeAutocomplete<LapEntity>('lap')}
            />

            <FormRowSubmit>
                <Button variant='outlined' disabled={isSubmitting} onClick={onCancel}>
                    {t('common:actions.cancel')}
                </Button>

                <Button type='submit' loading={isSubmitting}>
                    {t('common:actions.submit')}
                </Button>
            </FormRowSubmit>
        </StyledForm>
    );
};

export { ChangeLapForm };
